import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { posts } from '../data/blog/posts';
import { formatDate } from '../utils/date';
import { BlogPost } from '../types/blog';

export function Blog() {
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'corporate' | 'data-migration' | 'engineering'>('all');

  const filteredPosts = posts.filter(post => 
    selectedCategory === 'all' || post.category === selectedCategory
  );

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'data-migration', label: 'Data Migration Insights' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'corporate', label: 'Corporate' }
  ];

  const getCategoryStyle = (category: string) => {
    switch(category) {
      case 'engineering':
        return 'bg-blue-100 text-blue-800';
      case 'data-migration':
        return 'bg-purple-100 text-purple-800';
      case 'corporate':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch(category) {
      case 'engineering':
        return 'Engineering';
      case 'data-migration':
        return 'Data Migration';
      case 'corporate':
        return 'Corporate';
      default:
        return category;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gray-50 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Category Filter */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id as typeof selectedCategory)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-[#8000FF] text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid gap-8">
          {filteredPosts.map((post) => (
            <Link 
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="block bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-200 hover:shadow-xl hover:-translate-y-1"
            >
              <div className="grid md:grid-cols-[490px,1fr]">
                <div className="relative h-64 md:h-full">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <time className="text-sm text-gray-500">
                      {formatDate(post.date)}
                    </time>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryStyle(post.category)}`}>
                      {getCategoryLabel(post.category)}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-700">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 mb-6 line-clamp-3">{post.excerpt}</p>
                  <span className="inline-flex items-center text-sm font-semibold text-gray-900 hover:text-gray-700">
                    Read more
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}